import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, FileText, Calendar, Phone, ArrowRight } from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <img 
                src="/images/logo.png" 
                alt="Expert-Handyman" 
                className="h-16 w-auto object-contain group-hover:scale-105 transition-transform"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors ${isActive('/') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="nav-home"
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className={`text-sm font-medium transition-colors ${isActive('/services') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="nav-services"
              >
                Services
              </Link>
              <Link 
                to="/about" 
                className={`text-sm font-medium transition-colors ${isActive('/about') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="nav-about"
              >
                About
              </Link>
              <Link 
                to="/gallery" 
                className={`text-sm font-medium transition-colors ${isActive('/gallery') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="nav-gallery"
              >
                Our Work
              </Link>
              <Link 
                to="/quote" 
                className={`text-sm font-medium transition-colors ${isActive('/quote') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="nav-quote"
              >
                Get Quote
              </Link>
              
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-full gap-2" data-testid="user-menu-trigger">
                      <User className="w-4 h-4" />
                      <span className="max-w-24 truncate">{user?.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=quotes')} data-testid="menu-quotes">
                      <FileText className="w-4 h-4 mr-2" />
                      My Quotes
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=bookings')} data-testid="menu-bookings">
                      <Calendar className="w-4 h-4 mr-2" />
                      My Bookings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-3">
                  <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login">
                    Login
                  </Button>
                  <Button 
                    className="rounded-full bg-primary hover:bg-primary/90"
                    onClick={() => navigate('/register')}
                    data-testid="nav-register"
                  >
                    Get Started
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/" 
                className="block py-2 text-foreground"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-home"
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className="block py-2 text-foreground"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-services"
              >
                Services
              </Link>
              <Link 
                to="/about" 
                className="block py-2 text-foreground"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-about"
              >
                About
              </Link>
              <Link 
                to="/gallery" 
                className="block py-2 text-foreground"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-gallery"
              >
                Our Work
              </Link>
              <Link 
                to="/quote" 
                className="block py-2 text-foreground"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-quote"
              >
                Get Quote
              </Link>
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="block py-2 text-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-dashboard"
                  >
                    Dashboard
                  </Link>
                  <button 
                    className="block py-2 text-destructive"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    data-testid="mobile-nav-logout"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    data-testid="mobile-nav-login"
                  >
                    Login
                  </Button>
                  <Button 
                    className="flex-1 rounded-full"
                    onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                    data-testid="mobile-nav-register"
                  >
                    Register
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1 pb-14 md:pb-0">
        {children}
      </main>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/447300405045?text=Hi%2C%20I%27m%20interested%20in%20getting%20a%20quote%20from%20Expert-Handyman."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-green-500 hover:bg-green-600 rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-all hover:scale-105"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-white font-semibold text-sm">WhatsApp</span>
      </a>

      {/* Sticky Mobile CTA - hidden on quote page and admin pages */}
      {!['/quote', '/admin'].includes(location.pathname) && (
        <div className="fixed bottom-0 left-0 right-0 z-[55] md:hidden">
          <div className="flex bg-primary shadow-[0_-4px_20px_rgba(0,0,0,0.15)]">
            <a
              href="tel:07300405045"
              className="flex-1 flex items-center justify-center gap-2 py-3 text-white text-sm font-semibold border-r border-white/20 active:bg-white/10 transition-colors"
              data-testid="sticky-cta-call"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
            <Link
              to="/quote"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#84cc16] text-white text-sm font-bold active:brightness-90 transition-all"
              data-testid="sticky-cta-quote"
            >
              Free Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2 flex flex-col items-center text-center">
              <div className="mb-4">
                <img 
                  src="/images/logo.png" 
                  alt="Expert-Handyman" 
                  className="h-40 md:h-80 w-auto object-contain rounded-lg"
                />
              </div>
              <p className="text-primary-foreground/70 max-w-md">
                Your trusted local experts for all home repairs and improvements across Greater Manchester & Cheshire.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 font-['Outfit']">Services</h4>
              <ul className="space-y-2 text-primary-foreground/70">
                <li>Decorating</li>
                <li>Carpentry</li>
                <li>Outdoors & Garden</li>
                <li>Electrics</li>
                <li>Plastering & Tiling</li>
                <li>General Tasks</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 font-['Outfit']">Contact</h4>
              <ul className="space-y-2 text-primary-foreground/70">
                <li>support@expert-handyman.co.uk</li>
                <li>07300 405045</li>
                <li>Mon-Sat: 8am - 6pm</li>
              </ul>
              <div className="mt-4 space-y-2">
                <Link 
                  to="/handyman/apply" 
                  className="inline-flex items-center justify-center px-4 py-2 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-colors text-sm"
                  data-testid="footer-join-handyman"
                >
                  Join as Handyman →
                </Link>
                <Link 
                  to="/admin" 
                  className="block text-sm text-white/50 hover:text-white/80"
                  data-testid="footer-admin"
                >
                  Admin Access
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-primary-foreground/50 text-sm">
            <span>&copy; {new Date().getFullYear()} Expert-Handyman. All rights reserved.</span>
            <Link 
              to="/sitemap" 
              className="hover:text-white transition-colors"
              data-testid="footer-sitemap"
            >
              Sitemap
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
